import { useEffect, useId, useRef, useState } from 'react'
import { smooth } from '@domain/rules/noise.ts'
import { startNoiseMeter, type NoiseFailure, type NoiseMeter } from '@adapters/audio/noiseMeter.ts'
import { es } from '../strings/es.ts'

/**
 * Donde un adulto pone la raya.
 *
 * Tiene que ser aquí y no en un número de fábrica porque el micrófono de una
 * tablet no está calibrado: los decibelios que diera serían inventados, y una
 * cocina con la campana puesta no se parece en nada a un salón por la tarde.
 * Así que la raya se pone a ojo, con la barra del ruido de ahora justo debajo,
 * mientras en la habitación se habla al volumen que de verdad parece bien.
 *
 * Es el mismo truco que la foto de «así queda hecho»: en vez de discutir sobre
 * una palabra —«alto», «bajo»— se deja fijado un criterio que después se puede
 * comprobar sin discutir.
 */
export function SemaforoSetup({
  onStart,
  onBack,
}: {
  onStart: (minutes: number, limit: number) => void
  onBack: () => void
}) {
  const [minutes, setMinutes] = useState(15)
  const [limit, setLimit] = useState(0.35)
  const [level, setLevel] = useState(0)
  const [failure, setFailure] = useState<NoiseFailure | null>(null)
  const nivel = useRef(0)
  const minutesId = useId()
  const limitId = useId()

  useEffect(() => {
    let meter: NoiseMeter | null = null
    let cancelled = false

    void (async () => {
      const started = await startNoiseMeter((sample) => {
        nivel.current = smooth(nivel.current, sample)
        setLevel(nivel.current)
      })
      if (cancelled) {
        if (started.ok) started.meter.stop()
        return
      }
      if (started.ok) meter = started.meter
      else setFailure(started.failure)
    })()

    return () => {
      cancelled = true
      meter?.stop()
    }
  }, [])

  return (
    <div className="mx-auto max-w-2xl px-5 pt-5 pb-16">
      <button type="button" onClick={onBack} className="text-muted mb-5 text-sm font-bold">
        {es.today.back}
      </button>
      <h1 className="mb-2 text-2xl font-extrabold tracking-tight">{es.semaforo.setup}</h1>
      <p className="text-muted mt-0 mb-6 text-sm">{es.semaforo.privacy}</p>

      {failure ? (
        <p role="alert" className="text-coral mb-6 font-bold">
          {failure === 'sin-permiso' ? es.semaforo.noPermission : es.semaforo.noMic}
        </p>
      ) : null}

      <label htmlFor={minutesId} className="mb-1 block font-bold">
        {es.semaforo.minutes}
      </label>
      <input
        id={minutesId}
        type="number"
        min={1}
        max={120}
        value={minutes}
        onChange={(e) => {
          setMinutes(Number(e.target.value))
        }}
        className="border-rule bg-surface mb-6 w-28 rounded-lg border-2 p-3"
      />

      <label htmlFor={limitId} className="mb-1 block font-bold">
        {es.semaforo.limit}
      </label>
      <p className="text-muted mt-0 mb-3 text-sm">{es.semaforo.limitHelp}</p>
      <input
        id={limitId}
        type="range"
        min={5}
        max={100}
        value={Math.round(limit * 100)}
        onChange={(e) => {
          setLimit(Number(e.target.value) / 100)
        }}
        className="mb-4 w-full"
      />

      <p className="text-muted m-0 mb-1 text-sm font-bold">{es.semaforo.now}</p>
      <div className="border-rule bg-surface relative mb-8 h-8 overflow-hidden rounded-full border-2">
        <div
          className={`absolute inset-y-0 left-0 ${level >= limit ? 'bg-coral' : 'bg-mint'}`}
          style={{ width: `${String(Math.round(Math.min(1, level) * 100))}%` }}
        />
        <div
          className="bg-ink absolute inset-y-0 w-1"
          style={{ left: `${String(Math.round(limit * 100))}%` }}
        />
      </div>

      <p className="text-muted mt-0 mb-5 text-sm font-bold">{es.semaforo.neverPunishes}</p>

      <button
        type="button"
        disabled={failure !== null}
        onClick={() => {
          onStart(minutes, limit)
        }}
        className="bg-teal w-full rounded-xl px-6 py-4 text-lg font-extrabold text-white disabled:opacity-40"
      >
        {es.semaforo.start}
      </button>
    </div>
  )
}
